/**
 * useActivityCentrePanel
 *
 * Panel open/tab state for the ActivityCentre sidebar, backed by a
 * URL search param so the state survives reloads and is shareable.
 *
 * ## Design
 *
 * The products page and the sidebar are rendered as two independent
 * React islands that never share a parent component. Instead of hoisting
 * a context provider above both, we use the URL as the shared source of
 * truth and a window-level custom event as the signal.
 *
 * | mechanism                  | purpose                                          |
 * |----------------------------|--------------------------------------------------|
 * | `?activity=<tab>` URL param| Source of truth; enables deep-linking/sharing    |
 * | `popstate` listener        | React when browser/back-forward changes the URL  |
 * | `activity-centre:change`   | React to in-page `openActivityCentrePanel` calls |
 *
 * ## Contract
 *
 * - Panel is **open** iff the URL param is one of `ACTIVITY_CENTRE_TABS`.
 * - The last-viewed tab is remembered in component state, so closing and
 *   reopening via the sidebar button returns to that tab.
 * - Writes use `history.replaceState` so the bar doesn't stack entries
 *   in the browser history on every tab click.
 */

// React
import { useCallback, useEffect, useMemo, useState } from 'react';

// Hooks and utilities
import { ACTIVITY_CENTRE_TABS, type ActivityCentreTab } from './constants';

const URL_PARAM = 'activity';
const CHANGE_EVENT = 'activity-centre:change';
const DEFAULT_TAB: ActivityCentreTab = 'processing';

function readParam(): ActivityCentreTab | null {
  const value = new URLSearchParams(window.location.search).get(URL_PARAM);
  return ACTIVITY_CENTRE_TABS.includes(value as ActivityCentreTab) ? (value as ActivityCentreTab) : null;
}

function writeParam(tab: ActivityCentreTab | null) {
  const url = new URL(window.location.href);
  if (tab === null) url.searchParams.delete(URL_PARAM);
  else url.searchParams.set(URL_PARAM, tab);
  window.history.replaceState({}, '', url.toString());
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export interface ActivityCentrePanelState {
  /** Whether the panel is currently visible (derived from the URL param). */
  open: boolean;
  /** Active tab. When `open` is false this reflects the last-viewed tab
   *  so reopening returns to the same place. */
  tab: ActivityCentreTab;
  /** Open or close the panel. Closing clears the URL param. */
  setOpen: (open: boolean) => void;
  /** Switch tabs. If the panel is open the URL param updates; otherwise
   *  the hook just remembers the choice for the next open. */
  setTab: (tab: ActivityCentreTab) => void;
}

export function useActivityCentrePanel(): ActivityCentrePanelState {
  const [tabFromUrl, setTabFromUrl] = useState<ActivityCentreTab | null>(() => readParam());
  const [lastTab, setLastTab] = useState<ActivityCentreTab>(tabFromUrl ?? DEFAULT_TAB);

  useEffect(() => {
    const sync = () => {
      const next = readParam();
      setTabFromUrl(next);
      if (next) setLastTab(next);
    };
    window.addEventListener('popstate', sync);
    window.addEventListener(CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(CHANGE_EVENT, sync);
    };
  }, []);

  const setOpen = useCallback((next: boolean) => writeParam(next ? lastTab : null), [lastTab]);
  const setTab = useCallback((next: ActivityCentreTab) => {
    setLastTab(next);
    if (tabFromUrl !== null) writeParam(next);
  }, [tabFromUrl]);

  return useMemo<ActivityCentrePanelState>(
    () => ({ open: tabFromUrl !== null, tab: tabFromUrl ?? lastTab, setOpen, setTab }),
    [tabFromUrl, lastTab, setOpen, setTab],
  );
}

/**
 * Opens the activity centre panel from any React island on the page.
 * Writes the URL param and broadcasts `activity-centre:change` so any
 * mounted `useActivityCentrePanel` instance picks up the new state.
 *
 * @example
 *   onClick={() => openActivityCentrePanel('processing')}
 */
export function openActivityCentrePanel(tab: ActivityCentreTab = DEFAULT_TAB) {
  writeParam(tab);
}
